import { createStore } from "../../util/zustand";
import { Transition } from "@headlessui/react";
import { XIcon } from "lucide-react";
import { Fragment } from "react";
import { v4 as uuidv4 } from "uuid";
import { createPortal } from "react-dom";

const DEFAULT_TOAST_DURATION = 5000;

type ToastCTA = {
    text: string;
    action: () => void;
};

type Position = "bottom" | "right";

type Toast = {
    id: string;
    text: string;
    position: Position;
    action?: ToastCTA;
    timeoutId: NodeJS.Timeout;
};

const store = createStore<{ toasts: Toast[] }>({ toasts: [] });

function show(text: string, options?: { duration?: number; position?: Position; action?: ToastCTA }) {
    const id = uuidv4();

    const toast: Toast = {
        id,
        text: text,
        position: options?.position || "bottom",
        action: options?.action,
        timeoutId: setTimeout(() => {
            dismiss(id);
        }, options?.duration ?? DEFAULT_TOAST_DURATION)
    };

    store.setState(prev => ({
        toasts: [...prev.toasts, toast]
    }));
}

function dismiss(id: string) {
    store.setState(prev => ({
        toasts: prev.toasts.filter(toast => toast.id !== id)
    }));
}

const ToastContent = (props: { toast: Toast }) => {
    const { toast } = props;
    const axis = toast.position == "right" ? "x" : "y";

    // If the user hovers clear the dismissal timeout and let them close it manually
    const handleMouseEnter = () => {
        clearTimeout(toast.timeoutId);
    };

    return (
        <Transition
            appear
            show={true}
            as={Fragment}
            enter="transform transition ease-out duration-500"
            enterFrom={`opacity-0 translate-${axis}-4`}
            enterTo={`opacity-100 translate-${axis}-0`}
            leave="transition ease-in duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div className="pointer-events-auto max-h-8 w-fit rounded bg-stone-950 select-none" onMouseEnter={handleMouseEnter}>
                <div className="flex items-center justify-between px-3 py-1 text-white">
                    <div className="flex">
                        <h3>{toast.text}</h3>

                        {toast.action && (
                            <div
                                className="ml-3 cursor-pointer font-bold text-white underline"
                                onClick={() => {
                                    toast.action?.action();
                                    dismiss(toast.id);
                                }}
                            >
                                {toast.action?.text}
                            </div>
                        )}
                    </div>

                    <XIcon
                        className="ml-1.5 size-4 text-stone-400 hover:text-stone-50"
                        onClick={() => {
                            dismiss(toast.id);
                        }}
                    />
                </div>
            </div>
        </Transition>
    );
};

// Main component that goes at the highest level of the app as <MagicToast.Container /> (currently in App.tsx)
function Container() {
    const toasts = store.use(s => s.toasts);

    const bottomToasts = toasts.filter(toast => toast.position === "bottom");
    const rightToasts = toasts.filter(toast => toast.position === "right");

    return createPortal(
        <>
            {/* Top Right toasts container */}
            <div className="pointer-events-none fixed inset-0 z-50 flex items-start justify-end px-3 py-2">
                <div className="flex w-full flex-col items-end space-y-4">
                    {rightToasts.map(toast => (
                        <ToastContent key={toast.id} toast={toast} />
                    ))}
                </div>
            </div>

            {/* Bottom toasts container */}
            <div className="pointer-events-none fixed inset-0 z-50 flex items-end px-6 py-6">
                <div className="flex w-full flex-col items-center space-y-4">
                    {bottomToasts.map(toast => (
                        <ToastContent key={toast.id} toast={toast} />
                    ))}
                </div>
            </div>
        </>,
        document.body
    );
}

export default {
    Container,
    show,
    dismiss
};
